import { useEffect } from "react";
import { useAddElementSkeletonTracker } from "../../context/addElementSkeletonContext";
import { V3EmptyState } from "./V3EmptyStateScreen";
import { EmptyStateBtnPlacer } from "./V3EmptyStateScreen.styles";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { V3EmptyStateBtn } from "../ReusableStyledComponents/EmptyStateButton";

const getEmptyCollectionTitle = (isCollection: boolean, isPrivate: boolean, isDefaultCollection: boolean) => {
    if (isPrivate) {
        if (isDefaultCollection) {
            return "Get started by adding your work samples";
        } else {
            return <>Group your work into a focused collection</>;
        }
    }
    return `This ${isCollection ? "collection" : "portfolio"} is work in progress, please check back later`;
};

const getEmptyCollectionDescription = (isPrivate: boolean) => {
    if (isPrivate)
        return "Add work samples to this collection to group them by topic, by client, or by whatever you want to feature. You can select from the pieces already in your archive, or import and upload new ones.";
    return "If you are the owner, then you can add content items now.";
};

interface V3ProfileEmptyCollectionProps {
    isCollection?: boolean;
    isPrivate?: boolean;
    isDefaultCollection?: boolean;
}

export const V3ProfileEmptyCollection = ({ isCollection = false, isPrivate = false, isDefaultCollection = false }: V3ProfileEmptyCollectionProps) => {
    const { setOnScreen, setAddContentOpen } = useAddElementSkeletonTracker();

    useEffect(() => {
        setOnScreen(true);
    }, []);

    const isProfileDefaultCollection = isPrivate && !isCollection && isDefaultCollection;
    const isProfileRegularCollection = isPrivate && !isCollection && !isDefaultCollection;
    const isCollectionPrivatePage = isPrivate && isCollection;

    return (
        <V3EmptyState
            title={getEmptyCollectionTitle(isCollection, isPrivate, isDefaultCollection)}
            description={getEmptyCollectionDescription(isPrivate)}
            svgIcon={
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="80" height="80" fill="#F4F4F6" />
                    <path
                        d="M27 26.3333V19.6667C27 17.8986 27.7024 16.2029 28.9526 14.9526C30.2029 13.7024 31.8986 13 33.6667 13H60.3333C62.1014 13 63.7971 13.7024 65.0474 14.9526C66.2976 16.2029 67 17.8986 67 19.6667V46.3333C67 48.1015 66.2976 49.7971 65.0474 51.0474C63.7971 52.2976 62.1014 53 60.3333 53H53.6667"
                        fill="#DDDFE3"
                    />
                    <rect x="13" y="18.5" width="48" height="48" rx="8" fill="#F4F4F6" />
                    <path
                        d="M50.3333 22.5H23.6667C19.9848 22.5 17 25.4848 17 29.1667V55.8333C17 59.5152 19.9848 62.5 23.6667 62.5H50.3333C54.0152 62.5 57 59.5152 57 55.8333V29.1667C57 25.4848 54.0152 22.5 50.3333 22.5Z"
                        stroke="#ABB0BA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="8 8"
                    />
                    <path d="M30 49L37 37L44 49" stroke="#ABB0BA" strokeWidth="2" />
                </svg>
            }
            maxWidth={600}
        >
            {isPrivate && (
                <EmptyStateBtnPlacer>
                    <div>
                        <span data-intercom-target={isDefaultCollection ? "DefaultCollectionAddCTA" : undefined}>
                            <V3EmptyStateBtn
                                text={`Add work samples${isDefaultCollection ? "" : " to this collection"}`}
                                onClick={() => setAddContentOpen(true)}
                                autoWidth
                                minWidth={isDefaultCollection ? 200 : 280}
                            />
                        </span>
                    </div>
                    <div style={{ width: "100%", maxWidth: 627 }}>
                        <VerticalSpacing bottom={20} top={40}>
                            <V3BodyGrey48>...or learn more in this quick video:</V3BodyGrey48>
                        </VerticalSpacing>
                        {isProfileDefaultCollection && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: `<div class="wistia_responsive_padding" style="padding:61.46% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><iframe src="https://fast.wistia.net/embed/iframe/ostqeulfg1?web_component=true&seo=true" title="Quick overview Video" allow="autoplay; fullscreen" allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed" width="100%" height="100%"></iframe></div></div><script src="https://fast.wistia.net/player.js" async></script>`
                                }}
                            />
                        )}
                        {isProfileRegularCollection && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: `<div class="wistia_responsive_padding" style="padding:61.46% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><iframe src="https://fast.wistia.net/embed/iframe/mq27qakf0l?web_component=true&seo=true" title="Collections on portfolio_empty Video" allow="autoplay; fullscreen" allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed" width="100%" height="100%"></iframe></div></div><script src="https://fast.wistia.net/player.js" async></script>`
                                }}
                            />
                        )}
                        {isCollectionPrivatePage && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: `<div class="wistia_responsive_padding" style="padding:61.46% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><iframe src="https://fast.wistia.net/embed/iframe/q0fy6m3s85?web_component=true&seo=true" title="Collections not on portfolio_empty Video" allow="autoplay; fullscreen" allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed" width="100%" height="100%"></iframe></div></div>
<script src="https://fast.wistia.net/player.js" async></script>`
                                }}
                            />
                        )}
                    </div>
                </EmptyStateBtnPlacer>
            )}
        </V3EmptyState>
    );
};
